import { can, formatDate } from '@clim/client-core';
import {
  PAYMENT_CHANGEABLE_STATUSES,
  PAYMENT_SUMMARY_LABELS,
  PAYMENT_SUMMARY_TONES,
  formatEuros,
  paymentMachine,
  parseEurosToCents,
  type DossierRecord,
  type PaymentRecord,
  type PaymentStatus,
  uuidv7,
} from '@clim/domain';
import { useEffect, useState } from 'react';
import { errorMessage, fieldErrors } from '../lib/errors';
import { useEngine, useReady } from '../lib/session';
import { useOnline } from '../lib/use-online';
import { ReasonDialog } from './Interventions';
import { Empty, ErrorBox, Field, Input, Modal, Textarea } from './ui';

const TONE: Record<PaymentStatus, string> = {
  pending: 'warning',
  processing: 'progress',
  to_remit: 'warning',
  succeeded: 'success',
  failed: 'danger',
  canceled: 'neutral',
  partially_refunded: 'info',
  refunded: 'neutral',
  disputed: 'danger',
};

export function PaymentBadge({ p }: { p: PaymentRecord }) {
  const label = p.status === 'pending' && p.methodKind !== 'stripe' ? 'À confirmer' : paymentMachine.labels[p.status];
  return <span className={`badge tone-${TONE[p.status]}`}>{label}</span>;
}

/** Paiements du dossier (§10) : statut calculé, payé, en cours, solde ; encaissement hors ligne. */
export function DossierPayments({ dossier }: { dossier: DossierRecord }) {
  const engine = useEngine();
  const { me } = useReady();
  const [collecting, setCollecting] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  if (!can(me, 'payment.read') && !can(me, 'payment.collect')) return null;
  const summary = engine.paymentSummary(dossier.id);
  const payments = engine.paymentsOf(dossier.id);
  const canCollect = can(me, 'payment.collect') && dossier.status !== 'cancelled';

  return (
    <div className="card">
      <div className="card-head">
        <h2>
          Paiement {summary && <span className={`badge tone-${PAYMENT_SUMMARY_TONES[summary.status]}`}>{PAYMENT_SUMMARY_LABELS[summary.status]}</span>}
        </h2>
        {canCollect && summary && summary.remainingCents > 0 && (
          <button className="btn small primary" onClick={() => setCollecting(true)}>
            + Encaisser
          </button>
        )}
      </div>
      {!summary ? (
        <Empty>Encaissement possible après la signature du bon de commande.</Empty>
      ) : (
        <>
          <dl className="money" aria-label="Récapitulatif du paiement">
            <div>
              <dt>Total TTC signé</dt>
              <dd>{formatEuros(summary.totalCents)}</dd>
            </div>
            <div>
              <dt>Encaissé</dt>
              <dd>{formatEuros(summary.paidCents)}</dd>
            </div>
            {summary.pendingCents > 0 && (
              <div>
                <dt>En attente de confirmation</dt>
                <dd>{formatEuros(summary.pendingCents)}</dd>
              </div>
            )}
            <div className="balance">
              <dt>Reste à encaisser</dt>
              <dd>{formatEuros(summary.remainingCents)}</dd>
            </div>
          </dl>
          {payments.length === 0 ? (
            <Empty>Aucun paiement.</Empty>
          ) : (
            <ul className="plain payments">
              {payments.map((p) => (
                <li key={p.id}>
                  <button type="button" className="row" onClick={() => setOpen(p.id)}>
                    <span className="row-main">
                      <strong>
                        {p.isDeposit ? 'Acompte · ' : ''}
                        {p.methodLabel} · {formatEuros(p.amountCents)}
                        {p.refundedCents > 0 && ` (remboursé ${formatEuros(p.refundedCents)})`}
                      </strong>
                      <span className="muted">
                        {p.receivedOn ? `${p.receivedOn.split('-').reverse().join('/')} · ` : ''}
                        encaissé par {p.collectedByName}
                        {p.reference ? ` · réf. ${p.reference}` : ''}
                        {p.confirmedByName && p.status === 'succeeded' && p.methodKind !== 'stripe' ? ` · confirmé par ${p.confirmedByName}` : ''}
                      </span>
                    </span>
                    <PaymentBadge p={p} />
                    {engine.syncState('payment', p.id) !== 'synced' && <span className="sync-tag">⟳</span>}
                  </button>
                  {p.methodKind === 'stripe' && p.status === 'pending' && <CheckoutLink p={p} />}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      {collecting && summary && <CollectDialog dossier={dossier} remaining={summary.remainingCents} hasPayments={payments.length > 0} onClose={() => setCollecting(false)} />}
      {open && <PaymentDialog id={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

/** Lien Stripe : ouvrir sur l'appareil (paiement dans l'application) ou envoyer au client. */
function CheckoutLink({ p }: { p: PaymentRecord }) {
  const { api } = useReady();
  const online = useOnline();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const renew = () => {
    setBusy(true);
    setError(null);
    api
      .request('POST', `/v1/payments/${p.id}/checkout`, {})
      .catch((e) => setError(errorMessage(e)))
      .finally(() => setBusy(false));
  };
  if (!p.checkoutUrl) {
    return (
      <div className="checkout">
        <span className="muted">{p.checkoutError ?? (online ? 'Lien de paiement en préparation…' : 'Lien de paiement créé à la prochaine connexion.')}</span>
        {online && p.checkoutError && (
          <button className="btn small" disabled={busy} onClick={renew}>
            Nouveau lien
          </button>
        )}
        <ErrorBox message={error} />
      </div>
    );
  }
  const url = p.checkoutUrl;
  return (
    <div className="checkout">
      <a className="btn small primary" href={url} target="_blank" rel="noreferrer">
        💳 Payer maintenant sur cet appareil
      </a>
      <button
        className="btn small"
        onClick={() => {
          if (navigator.share) void navigator.share({ title: 'Paiement sécurisé', url }).catch(() => undefined);
          else void navigator.clipboard.writeText(url).then(() => setCopied(true));
        }}
      >
        {copied ? 'Lien copié ✓' : '↗ Envoyer le lien au client'}
      </button>
      <span className="muted">Paiement sécurisé Stripe · lien valable jusqu’au {formatDate(p.checkoutExpiresAt!)}</span>
    </div>
  );
}

function CollectDialog({ dossier, remaining, hasPayments, onClose }: { dossier: DossierRecord; remaining: number; hasPayments: boolean; onClose(): void }) {
  const engine = useEngine();
  const methods = engine
    .list('payment_method')
    .filter((m) => m.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const [methodId, setMethodId] = useState(methods.find((m) => m.configured)?.id ?? '');
  const [amount, setAmount] = useState(String(remaining / 100).replace('.', ','));
  // the first partial payment is usually the deposit
  const [isDeposit, setIsDeposit] = useState(!hasPayments);
  const [receivedOn, setReceivedOn] = useState(new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState('');
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const m = methods.find((x) => x.id === methodId);
  const cents = parseEurosToCents(amount);
  const submit = () => {
    setError(null);
    setFields({});
    if (!cents) return setFields({ amountCents: 'Montant invalide' });
    engine
      .createPayment(dossier.id, { methodId, amountCents: cents, isDeposit, receivedOn: m?.kind === 'stripe' ? null : receivedOn, reference, comment })
      .then(onClose)
      .catch((e) => {
        setError(errorMessage(e));
        setFields(fieldErrors(e));
      });
  };
  return (
    <Modal title="Encaisser" onClose={onClose}>
      <div className="form">
        <div className="chips" role="radiogroup" aria-label="Moyen de paiement">
          {methods.map((x) => (
            <button
              key={x.id}
              type="button"
              role="radio"
              aria-checked={methodId === x.id}
              disabled={!x.configured}
              title={x.configured ? undefined : 'Pas encore configuré'}
              className={`chip${methodId === x.id ? ' on' : ''}${x.configured ? '' : ' muted'}`}
              onClick={() => setMethodId(x.id)}
            >
              {x.label}
            </button>
          ))}
        </div>
        {m?.instructions && <p className="muted">{m.instructions}</p>}
        <div className="grid2">
          <Field label="Montant (€)" error={fields.amountCents} hint={`Reste à encaisser : ${formatEuros(remaining)}`}>
            <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
          {m && m.kind !== 'stripe' && (
            <Field label="Date d’encaissement">
              <Input type="date" value={receivedOn} onChange={(e) => setReceivedOn(e.target.value)} />
            </Field>
          )}
        </div>
        <div className="chips">
          <button type="button" className="chip" onClick={() => setAmount(String(Math.round(remaining * 0.3) / 100).replace('.', ','))}>
            30 %
          </button>
          <button type="button" className="chip" onClick={() => setAmount(String(Math.round(remaining * 0.5) / 100).replace('.', ','))}>
            50 %
          </button>
          <button type="button" className="chip" onClick={() => setAmount(String(remaining / 100).replace('.', ','))}>
            Totalité
          </button>
        </div>
        <label className="check">
          <input type="checkbox" checked={isDeposit} onChange={(e) => setIsDeposit(e.target.checked)} />
          Acompte
        </label>
        {m?.requiresReference && (
          <Field label={m.kind === 'check' ? 'N° de chèque' : 'Référence'} error={fields.reference}>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} />
          </Field>
        )}
        <Field label="Commentaire (facultatif)">
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} />
        </Field>
        {m?.requiresRemittance && <p className="muted">« À remettre » jusqu’à la confirmation de la remise par un responsable.</p>}
        {m?.kind === 'stripe' && <p className="muted">Un lien de paiement sécurisé est créé (dès la connexion) : le client paie sur cet appareil ou reçoit le lien. Le paiement est confirmé par Stripe.</p>}
        <ErrorBox message={error} />
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Retour
          </button>
          <button className="btn primary" disabled={!methodId || !cents} onClick={submit}>
            Enregistrer {cents ? formatEuros(cents) : ''}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** Détail, journal, confirmation de remise, annulation motivée, remboursement. */
export function PaymentDialog({ id, onClose }: { id: string; onClose(): void }) {
  const engine = useEngine();
  const { me, api } = useReady();
  const online = useOnline();
  const p = engine.get('payment', id);
  const [history, setHistory] = useState<{ id: string; to: string; at: string; by: string; reason: string | null; comment: string | null; refundedCents: number }[] | null>(null);
  const [cancel, setCancel] = useState(false);
  const [refund, setRefund] = useState(false);
  const [changing, setChanging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (online) api.request<NonNullable<typeof history>>('GET', `/v1/payments/${id}/history`).then(setHistory, () => setHistory(null));
  }, [api, id, online, p?.version]);
  if (!p) return null;
  const d = engine.get('dossier', p.dossierId);
  const subjectOk = (perm: 'payment.cash.confirm' | 'payment.collect' | 'payment.refund' | 'payment.method_change') => can(me, perm);
  const canConfirm = subjectOk('payment.cash.confirm') && p.methodKind !== 'stripe' && ['to_remit', 'pending'].includes(p.status) && p.collectedById !== me.user.id;
  const run = (x: Promise<unknown>) => {
    setError(null);
    return x.catch((e) => setError(errorMessage(e)));
  };
  return (
    <Modal title={`${p.methodLabel} · ${formatEuros(p.amountCents)}`} onClose={onClose}>
      <p>
        <PaymentBadge p={p} /> {p.isDeposit && <span className="badge tone-info">Acompte</span>} <span className="muted">{d?.reference}</span>
      </p>
      <ul className="plain">
        <li>Encaissé par {p.collectedByName}{p.receivedOn ? ` le ${p.receivedOn.split('-').reverse().join('/')}` : ''}</li>
        {p.reference && <li>Référence : {p.reference}</li>}
        {p.comment && <li>Commentaire : {p.comment}</li>}
        {p.confirmedByName && <li>Confirmé par {p.confirmedByName} le {formatDate(p.confirmedAt!)}</li>}
        {p.refundedCents > 0 && <li>Remboursé : {formatEuros(p.refundedCents)}</li>}
        {p.replacedByPaymentId && (
          <li>
            Remplacé par : {engine.get('payment', p.replacedByPaymentId)?.methodLabel ?? 'un autre paiement'} (changement de moyen de paiement)
          </li>
        )}
        {p.replacesPaymentId && <li>Remplace : {engine.get('payment', p.replacesPaymentId)?.methodLabel ?? 'un paiement annulé'}</li>}
      </ul>
      {p.methodKind === 'stripe' && p.status === 'pending' && <CheckoutLink p={p} />}
      {p.methodKind !== 'stripe' && ['to_remit', 'pending'].includes(p.status) && !canConfirm && (
        <p className="muted">
          {p.collectedById === me.user.id
            ? 'Vous avez saisi ce paiement : une autre personne autorisée (administrateur ou superviseur) doit confirmer sa réception.'
            : 'À confirmer par un administrateur ou un superviseur (menu « Encaissements à valider »).'}
        </p>
      )}
      <ErrorBox message={error} />
      <div className="actions wrap">
        {canConfirm && (
          <button className="btn primary" onClick={() => void run(engine.transitionPayment(p.id, 'succeeded'))}>
            {p.status === 'to_remit' ? 'Confirmer la remise' : 'Confirmer la réception'}
          </button>
        )}
        {subjectOk('payment.method_change') && PAYMENT_CHANGEABLE_STATUSES.includes(p.status) && (
          <button className="btn" disabled={!online} title={online ? undefined : 'Nécessite une connexion'} onClick={() => setChanging(true)}>
            🔄 Changer le moyen de paiement
          </button>
        )}
        {subjectOk('payment.collect') && ['pending', 'to_remit', 'failed'].includes(p.status) && (
          <button className="btn danger-outline" onClick={() => setCancel(true)}>
            Annuler ce paiement
          </button>
        )}
        {subjectOk('payment.refund') && ['succeeded', 'partially_refunded'].includes(p.status) && (
          <button className="btn" disabled={!online} title={online ? undefined : 'Nécessite une connexion'} onClick={() => setRefund(true)}>
            Rembourser
          </button>
        )}
      </div>
      {history && (
        <details open>
          <summary>Journal</summary>
          <ol className="timeline">
            {history.map((h) => (
              <li key={h.id}>
                <strong>{paymentMachine.labels[h.to as PaymentStatus]}</strong>{' '}
                <span className="muted">
                  {formatDate(h.at)} · {h.by}
                </span>
                {h.reason && <div>Motif : {h.reason}</div>}
                {h.comment && <div className="pre">{h.comment}</div>}
              </li>
            ))}
          </ol>
        </details>
      )}
      {cancel && (
        <ReasonDialog
          title="Annuler ce paiement"
          category="payment_cancel"
          confirm="Annuler le paiement"
          onClose={() => setCancel(false)}
          onConfirm={(reasonId, comment) => engine.transitionPayment(p.id, 'canceled', { reasonId, comment })}
        />
      )}
      {refund && <RefundDialog p={p} onClose={() => setRefund(false)} />}
      {changing && <ChangeMethodDialog p={p} onClose={() => setChanging(false)} />}
    </Modal>
  );
}

/**
 * Administrateur : le client paiera finalement autrement. Le paiement (non encaissé) est remplacé par un paiement du
 * même montant avec le nouveau moyen ; un lien de paiement par carte est désactivé chez Stripe.
 */
function ChangeMethodDialog({ p, onClose }: { p: PaymentRecord; onClose(): void }) {
  const engine = useEngine();
  const { api } = useReady();
  const methods = engine
    .list('payment_method')
    .filter((m) => m.isActive && m.id !== p.methodId)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const [methodId, setMethodId] = useState(methods.find((m) => m.configured)?.id ?? '');
  const [receivedOn, setReceivedOn] = useState(new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState('');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const m = methods.find((x) => x.id === methodId);
  const submit = async () => {
    setBusy(true);
    setError(null);
    setFields({});
    try {
      await api.request('POST', `/v1/payments/${p.id}/change-method`, {
        newPaymentId: uuidv7(),
        methodId,
        receivedOn: m?.kind === 'stripe' ? null : receivedOn,
        reference: reference || null,
        comment: comment || null,
      });
      await engine.sync();
      onClose();
    } catch (e) {
      setError(errorMessage(e));
      setFields(fieldErrors(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title="Changer le moyen de paiement" onClose={onClose}>
      <div className="form">
        <p>
          Paiement actuel : <strong>{p.methodLabel}</strong> · {formatEuros(p.amountCents)} ({paymentMachine.labels[p.status].toLowerCase()})
        </p>
        <p className="muted small">
          Il sera annulé (motif « Changement de moyen de paiement », conservé dans l’historique) et remplacé par un paiement du même montant.
          {p.methodKind === 'stripe' && ' Le lien de paiement par carte envoyé au client sera désactivé : il ne pourra plus payer avec.'}
        </p>
        <div className="chips" role="radiogroup" aria-label="Nouveau moyen de paiement">
          {methods.map((x) => (
            <button
              key={x.id}
              type="button"
              role="radio"
              aria-checked={methodId === x.id}
              disabled={!x.configured}
              className={`chip${methodId === x.id ? ' on' : ''}${x.configured ? '' : ' muted'}`}
              onClick={() => setMethodId(x.id)}
            >
              {x.label}
            </button>
          ))}
        </div>
        {m && m.kind !== 'stripe' && (
          <Field label="Date d’encaissement">
            <Input type="date" value={receivedOn} onChange={(e) => setReceivedOn(e.target.value)} />
          </Field>
        )}
        {m?.requiresReference && (
          <Field label={m.kind === 'check' ? 'N° de chèque' : 'Référence'} error={fields.reference}>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} />
          </Field>
        )}
        <Field label="Précision (facultatif)">
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Ex. : le client préfère régler en espèces" />
        </Field>
        {m?.kind === 'stripe' && <p className="muted small">Un nouveau lien de paiement par carte sera créé.</p>}
        <ErrorBox message={error} />
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Retour
          </button>
          <button className="btn primary" disabled={!methodId || busy} onClick={() => void submit()}>
            Remplacer par {m?.label ?? '…'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function RefundDialog({ p, onClose }: { p: PaymentRecord; onClose(): void }) {
  const { api } = useReady();
  const engine = useEngine();
  const max = p.amountCents - p.refundedCents;
  const [amount, setAmount] = useState(String(max / 100).replace('.', ','));
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const cents = parseEurosToCents(amount);
  return (
    <Modal title="Rembourser" onClose={onClose}>
      <div className="form">
        <Field label="Montant (€)" hint={`Maximum ${formatEuros(max)}`}>
          <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <Field label="Motif (obligatoire)">
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} />
        </Field>
        {p.methodKind === 'stripe' && <p className="muted">Le remboursement est demandé à Stripe ; le statut se met à jour à sa confirmation.</p>}
        <ErrorBox message={error} />
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Retour
          </button>
          <button
            className="btn primary"
            disabled={!cents || cents > max || comment.trim().length < 3}
            onClick={() =>
              api
                .request('POST', `/v1/payments/${p.id}/refund`, { amountCents: cents, comment })
                .then(() => engine.sync())
                .then(onClose)
                .catch((e) => setError(errorMessage(e)))
            }
          >
            Rembourser {cents ? formatEuros(cents) : ''}
          </button>
        </div>
      </div>
    </Modal>
  );
}
